import { FormControl, MenuItem, Select } from "@mui/material"
import type React from "react"
import { extraColors } from "../../utils/theme"
import { useChat } from "./ChatContext"
import type { ChatModelOption, ModelId } from "./types"

interface ModelSelectProps {
  value: ModelId | null
  onChange: (model: ModelId) => void
  options: ChatModelOption[]
  disabled?: boolean
  fullWidth?: boolean
  placeholder: string
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  fullWidth = false,
  placeholder,
}) => (
  <FormControl
    size="small"
    fullWidth={fullWidth}
    sx={fullWidth ? undefined : { minWidth: 220 }}
  >
    <Select
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      displayEmpty
      renderValue={selected => {
        if (selected === "") {
          return placeholder
        }

        return options.find(option => option.id === selected)?.label ?? selected
      }}
      variant="outlined"
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        color: extraColors.white,
        borderRadius: "8px",
        fontSize: 13,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: extraColors.translucentWhite,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: extraColors.status.navyLight,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: extraColors.status.navyLight,
        },
        "& .MuiSelect-icon": { color: extraColors.medium },
        "&.Mui-disabled": { opacity: 0.5 },
        "& .MuiSelect-select": { py: "6px" },
      }}
      MenuProps={{
        slotProps: {
          paper: {
            sx: {
              backgroundColor: extraColors.dark5,
              color: extraColors.white,
              border: `1px solid ${extraColors.translucentWhite}`,
              "& .MuiMenuItem-root": {
                fontSize: 13,
                "&:hover": { backgroundColor: extraColors.translucentWhite },
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "&:hover": { backgroundColor: extraColors.translucentWhite },
                },
              },
            },
          },
        },
      }}
    >
      {options.length === 0 ? (
        <MenuItem disabled value="">
          {placeholder}
        </MenuItem>
      ) : (
        options.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))
      )}
    </Select>
  </FormControl>
)

/** Session-scoped picker — lives next to the chat input. Disabled while streaming. */
export const ChatModelPicker: React.FC = () => {
  const {
    selectedModel,
    setModel,
    isStreaming,
    availableModels,
    isLoadingModels,
    hasModelsError,
  } = useChat()

  const placeholder = hasModelsError
    ? "Models unavailable"
    : isLoadingModels
      ? "Loading models..."
      : "Select a model"

  return (
    <ModelSelect
      value={selectedModel}
      onChange={setModel}
      options={availableModels}
      disabled={isStreaming || isLoadingModels || hasModelsError}
      placeholder={placeholder}
    />
  )
}

/** Default-model picker — lives in the sidebar, persisted across sessions. */
export const DefaultModelPicker: React.FC = () => {
  const {
    defaultModel,
    setDefaultModel,
    availableModels,
    isLoadingModels,
    hasModelsError,
  } = useChat()

  const placeholder = hasModelsError
    ? "Models unavailable"
    : isLoadingModels
      ? "Loading models..."
      : "Select a default"

  return (
    <ModelSelect
      value={defaultModel}
      onChange={setDefaultModel}
      options={availableModels}
      disabled={isLoadingModels || hasModelsError}
      fullWidth
      placeholder={placeholder}
    />
  )
}
